import React from 'react';
import { Link } from 'react-router-dom';
import { useThrottledToast } from '../../hooks/useThrottledToast';

import { ArrowLeft } from '../../components/icons/arrow-left';
import { Switch } from '../../components/common/switch';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation } from '../../services/users.api';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';

const ColorCode = () => {
  const tool = 'color_code';
  const isActiveByTariff = useTariffToolCheck(tool);
  const dispatch = useAppDispatch();
  const userData = useAppSelector(selectUser);
  const [changeUser] = useChangeUserMutation();
  const showToast = useThrottledToast();

  const handleCheck = async () => {
    try {
      if (!isActiveByTariff) return;
      if (!userData) return;
      if (userData?.active_psy_tools?.includes(tool)) {
        const arr = userData?.active_psy_tools.filter((el) => el !== tool);
        dispatch(setUserData({ ...userData, active_psy_tools: arr }));
        await changeUser({
          id: userData.id,
          active_psy_tools: arr,
        });
      } else {
        dispatch(
          setUserData({
            ...userData,
            active_psy_tools: [...userData?.active_psy_tools, tool],
          }),
        );
        await changeUser({
          id: userData.id,
          active_psy_tools: [...userData?.active_psy_tools, tool],
        });
      }
      showToast('success');
    } catch (error) {
      showToast('error');
    }
  };

  return (
    <div className="pb-[80px]">
      <h2 className="flex items-center gap-[14px] font-comfortaa font-semibold text-xl text-blackBase mb-5">
        <Link to={'/tools'} className="group">
          <ArrowLeft className="fill-blackBase group-hover:fill-redBase" width={21} height={16} />
        </Link>
        <span>Цветовой код</span>
      </h2>
      <div className="space-y-6 text-sm text-greyBase leading-relaxed mb-8">
        <section>
          <h3 className="text-base font-semibold text-blackBase mb-2">Цель и назначение</h3>
          <p>
            «Цветовой Код» — это цифровой психодиагностический и терапевтический инструмент, интегрированный в онлайн-кабинет психолога на платформе ZiNGiZi. Он предназначен для быстрой оценки текущего эмоционального состояния клиента во время сессии и предоставления психологу интерактивных подсказок для углубления терапевтической работы.
          </p>
        </section>

        <div className="flex items-center gap-5 mb-[40px]">
          <Switch
            checked={userData?.active_psy_tools?.includes(tool)}
            disabled={!isActiveByTariff}
            setChecked={() => handleCheck()}
            label={'Использовать Цветовой код в видео-сессиях'}
          />
        </div>
        <section>
          <h3 className="text-base font-semibold text-blackBase mb-2">Теоретическая основа</h3>
          <p className="mb-3">Инструмент опирается на классические и современные разработки в области цветовой психологии:</p>
          <ul className="space-y-3">
            <li>
              <span className="font-semibold text-blackBase">Макс Люшер</span> швейцарский психолог, создатель цветового теста Люшера. Его гипотеза о том, что цветовые предпочтения отражают глубинные психофизиологические состояния, легла в основу методики.
            </li>
            <li>
              <span className="font-semibold text-blackBase">Владимир Элькин</span> — российский врач-психотерапевт, музыкальный терапевт, автор книг «Целительная магия цвета и звука» и «Театр цвета и мелодии ваших страстей». Он адаптировал и расширил подход Люшера, добавив терапевтический контекст и акцент на изменчивости цветовых выборов в зависимости от актуальных переживаний.
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-semibold text-blackBase mb-2">Как работает инструмент</h3>
          <p>Процесс использования состоит из четырёх этапов:</p>
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>
              <span className="font-semibold text-blackBase">Активация и инструкция</span>. Психолог запускает инструмент во время видео-сессии. Клиенту предлагается интуитивно расположить 8 цветовых карточек (красный, жёлтый, зелёный, синий, фиолетовый, коричневый, серый, черный) в порядке от самого предпочитаемого в данный момент, не пытаясь ассоциировать цвета ни с чем посторонним, – например, с цветом одежды или интерьера.
            </li>
            <li>
              <span className="font-semibold text-blackBase">Выбор цвета</span>. Клиент перетаскивает карточки в интерфейсе, что делает процесс интерактивным и вовлекающим. После завершения выбора система фиксирует последовательность.
            </li>
            <li>
              <span className="font-semibold text-blackBase">ИИ-анализ</span>. На основе заложенных в систему алгоритмов, опирающихся на таблицы интерпретаций Люшера и Элькина, ИИ-помощник генерирует:
              <ul className="list-disc pl-6 mt-2 space-y-1 text-greyBase text-[13px]">
                <li>
                  <span className="font-semibold text-blackBase">
                    Краткое диагностическое заключение
                  </span>{' '}
                  (3-5 предложений) о возможном эмоциональном состоянии, потребностях и внутренних конфликтах клиента.
                </li>
                <li>
                  <span className="font-semibold text-blackBase">
                    Список из 5-8 наводящих вопросов
                  </span>{' '}
                  для психолога, помогающих исследовать выявленные темы.
                </li>
              </ul>
            </li>
            <li>
              <span className="font-semibold text-blackBase">Терапевтическое использование</span>. Психолог получает структурированные подсказки на экране. Он может:
              <ul className="list-disc pl-6 mt-2 space-y-1 text-greyBase text-[13px]">
                <li>
                  Озвучить клиенту часть заключения для проверки гипотез («Вы отметили, что сейчас ищете покоя и поддержки. Это созвучно вашим ощущениям?»).
                </li>
                <li>Использовать предложенные вопросы для углубления диалога.</li>
                <li>
                  Опираться на рекомендации по техникам (дыхание, визуализация, работа с границами), которые система может предлагать в расширенном режиме.
                </li>
              </ul>
            </li>
          </ol>
        </section>

        <section>
          <h3 className="text-base font-semibold text-blackBase mb-2">Ключевые инновации и преимущества</h3>
          <ol className="list-decimal pl-5 space-y-4">
            <li>
              <span className="font-semibold text-blackBase">От диагностики к терапии.</span> «Цветовой Код» ZiNGiZi выходит за рамки классической диагностики, превращаясь в инструмент для живой терапевтической работы. Он даёт не просто «ярлык» состояния, а отправные точки для диалога.
            </li>
            <li>
              <span className="font-semibold text-blackBase">Компенсация субъективности метода.</span> Традиционный тест Люшера критиковали за субъективность интерпретаций и недостаточную эмпирическую базу. В данном инструменте:
              <ul className="list-disc pl-6 mt-2 space-y-1 text-greyBase text-[13px]">
                <li>ИИ-анализ стандартизирует первичную интерпретацию, минимизируя случайность.</li>
                <li>
                  Ключевая валидизация происходит не через слепое доверие алгоритму, а через диалог психолога с клиентом. Гипотезы, сгенерированные ИИ, проверяются и уточняются в реальном времени.
                </li>
              </ul>
            </li>
            <li>
              <span className="font-semibold text-blackBase">Интеграция в цифровую сессию.</span> Инструмент бесшовно встроен в процесс онлайн-консультации, не нарушая её потока. Визуальный интерфейс интуитивен, а анализ происходит за несколько секунд.
            </li>
            <li>
              <span className="font-semibold text-blackBase">Поддержка психолога.</span> Даже специалисты, не являющиеся экспертами в цветодиагностике, могут эффективно использовать инструмент. ИИ-помощник выступает в роли «второго пилота», предлагая гипотезы и вопросы, которые психолог может развивать исходя из своей основной методологии.
            </li>
          </ol>
        </section>

        <section>
          <h3 className="text-base font-semibold text-blackBase mb-2">Ответ на возможную критику</h3>
          <p>
            Инструмент сознательно учитывает историческую критику метода Люшера. Он не позиционируется как абсолютная истина или самостоятельный диагностический аппарат. Это — <span className="font-semibold text-blackBase">катализатор диалога</span>. Заключение ИИ рассматривается как набор рабочих гипотез, которые психолог и клиент совместно исследуют. Таким образом, инструмент использует силу цветового метода (быстрое погружение в бессознательные процессы), но снимает его основной недостаток, переводя фокус с фиксированной интерпретации на совместный исследовательский процесс.
          </p>
        </section>

        <section>
          <h3 className="text-base font-semibold text-blackBase mb-2">Заключение</h3>
          <p> «Цветовой Код» на платформе ZiNGiZi — это современный синтез классической цветовой психологии, цифрового интерфейса и искусственного интеллекта. Он предоставляет практикующему психологу не просто ещё один тест, а интерактивный рабочий модуль, который помогает быстро установить контакт с актуальным состоянием клиента, сформулировать гипотезы и углубить терапевтическую работу, делая сессию более насыщенной и продуктивной.
          </p>
        </section>
      </div>

      <div className="grid grid-cols-8 gap-3 mb-5">
        {['#D90018', '#42AA02', '#93532C', '#FFCD0B', '#51007E', '#013C7E', '#817F85', '#010001'].map((color, idx) => (
          <div
            key={idx}
            className="w-full aspect-[3/4] rounded-[12px]"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>


    </div>
  );
};

export default ColorCode;
